/**
 * ArtifactsPanel — the right-side "Files" panel.
 *
 * Renders two stacked sections inside a single scrollable area:
 *   1. **Artifacts**   — agent-generated outputs (XLSX, CSV, JSON …) backed
 *      by a row in the ``artifacts`` table. Open/reveal hit the
 *      ``/api/shortlists/artifacts/{id}/{action}`` endpoints.
 *   2. **Other Files** — everything else the conversation references:
 *      user-uploaded drops + paths the agent passed to file-handling tools
 *      (Read / Write / Edit / render_ui / upload_user_document …). Open/
 *      reveal hit the path-based ``/api/files/{open,reveal}`` endpoints.
 *
 * Supports ``embedded`` mode for use inside the unified RightPanel
 * (renders only the section list, no panel header / close button).
 */

import { useEffect, useMemo, useState } from "react";
import { useChatStore } from "@/stores/chatStore";
import { getAccessToken, isNativeHost } from "@/api/client";
import {
  convRelOf,
  downloadArtifactSmart,
  downloadPathSmart,
  openArtifactSmart,
  openPathSmart,
  revealArtifact,
  revealPath,
} from "@/api/files";
import type { ShareTarget } from "@/api/shares";
import { ShareDialog } from "./ShareDialog";
import { extractOtherFiles, extractWidgets, type OtherFile } from "./otherFiles";

const FILE_ICONS: Record<string, string> = {
  xlsx: "📊",
  xls: "📊",
  csv: "📄",
  json: "📋",
  pdf: "📕",
  doc: "📝",
  docx: "📝",
  txt: "📄",
  md: "📄",
  yaml: "📋",
  yml: "📋",
  html: "🌐",
  htm: "🌐",
  png: "🖼️",
  jpg: "🖼️",
  jpeg: "🖼️",
  gif: "🖼️",
  webp: "🖼️",
};

function iconFor(ext: string): string {
  return FILE_ICONS[ext.toLowerCase()] ?? "📎";
}

// Open/reveal go through the shared, AUTHED file helpers (@/api/files) — no
// unauthenticated /api/files/serve?path usage. Open: native OS app, else an
// authed blob (artifact download / tenant-scoped serve-conv). Reveal is
// native-only (the UI hides it in a browser).
async function artifactAction(artifactId: string, action: "open" | "reveal", filename?: string) {
  if (action === "open") return openArtifactSmart(artifactId, filename);
  return revealArtifact(artifactId);
}

async function pathAction(path: string, action: "open" | "reveal") {
  if (action === "open") return openPathSmart(path);
  return revealPath(path);
}

interface FileCardProps {
  name: string;
  ext: string;
  subtitle: string;
  onOpen: () => void;
  onReveal: () => void;
  /** When true the file no longer exists on disk — render a greyed,
   *  non-actionable "moved / removed" card (Chrome-downloads style)
   *  instead of a card whose buttons silently do nothing. */
  missing?: boolean;
  /** Override the extension-derived icon (e.g. a widget glyph). */
  icon?: string;
  /** When provided, the footer gains a "Save to Files" action — used by
   *  the Widgets section to pin a widget as an artifact. The handler may
   *  be async; the button shows a saving state while it resolves. */
  onSave?: () => void | Promise<void>;
  /** Browser-only explicit save-to-disk. Open previews renderable types in a
   *  tab, so without this a cloud user has no way to actually keep the file. */
  onDownload?: () => void;
  /** Browser-only org share link (ADR-0013; cloud enforces the actual gate). */
  onShare?: () => void;
}

function FileCard({ name, ext, subtitle, onOpen, onReveal, missing = false, icon, onSave, onDownload, onShare }: FileCardProps) {
  const [saving, setSaving] = useState(false);
  // "Show in folder" only works in the native desktop shell (it dispatches the
  // OS file manager server-side). In a browser — the cloud web app especially —
  // there's no file manager, so hide it; Open already falls back to a browser
  // preview / download. Download is the inverse: native users have the file on
  // disk already, so the explicit save action is browser-only. The footer
  // collapses entirely when nothing's left.
  const showReveal = isNativeHost();
  const showDownload = !!onDownload && !isNativeHost();
  const showShare = !!onShare && !isNativeHost();
  const hasFooter = !!onSave || showReveal || showDownload || showShare;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave?.();
    } finally {
      setSaving(false);
    }
  };

  if (missing) {
    return (
      <div
        className="rounded-xl border border-dashed border-space-30 bg-space-10/40"
        title="This file has been moved or removed"
      >
        <div className="w-full text-left px-3 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-space-20 flex items-center justify-center shrink-0">
            <span className="text-base grayscale opacity-50">{iconFor(ext)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-space-40 truncate line-through decoration-space-30">
              {name}
            </p>
            <p className="text-[10px] text-space-40 mt-0.5">File moved or removed</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="group rounded-xl border border-space-30 bg-surface hover:border-jove-60/40 hover:shadow-sm transition-all duration-150">
      <button
        onClick={onOpen}
        className={`w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-jove-10/50 transition-colors ${hasFooter ? "rounded-t-xl" : "rounded-xl"}`}
      >
        <div className="w-9 h-9 rounded-lg bg-jove-30 flex items-center justify-center shrink-0 group-hover:bg-jove-50 transition-colors">
          <span className="text-base">{icon ?? iconFor(ext)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-space truncate">{name}</p>
          <p className="text-[10px] text-space-40 mt-0.5">{subtitle}</p>
        </div>
        <svg className="w-4 h-4 text-space-40 group-hover:text-jove transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </button>
      {hasFooter && (
        <div className="flex border-t border-space-30/50 overflow-hidden rounded-b-xl divide-x divide-space-30/50">
          {onSave && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-space-40
                         hover:text-jove hover:bg-jove-10/50 transition-colors disabled:opacity-60 disabled:hover:bg-transparent"
              title="Add this widget to the Files panel as an artifact"
            >
              {saving ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              )}
              {saving ? "Saving…" : "Save to Files"}
            </button>
          )}
          {showShare && (
            <button
              onClick={onShare}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-space-40
                         hover:text-jove hover:bg-jove-10/50 transition-colors"
              title="Share a link with your team"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
              </svg>
              Share
            </button>
          )}
          {showDownload && (
            <button
              onClick={onDownload}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-space-40
                         hover:text-jove hover:bg-jove-10/50 transition-colors"
              title="Save this file to your computer"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download
            </button>
          )}
          {showReveal && (
            <button
              onClick={onReveal}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-space-40
                         hover:text-jove hover:bg-jove-10/50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
              </svg>
              Show in folder
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-baseline gap-2 px-1 pt-1 pb-1.5">
      <h4 className="text-[10px] font-bold tracking-widest uppercase text-space-50">{label}</h4>
      <span className="text-[10px] font-semibold text-space-40">{count}</span>
    </div>
  );
}

function otherFileSubtitle(f: OtherFile): string {
  const tag = f.ext ? `${f.ext.toUpperCase()} file` : "File";
  return f.source === "upload" ? `${tag} · Uploaded` : `${tag} · ${f.toolName ?? "Tool"}`;
}

/** Basename of a POSIX or Windows path. */
function basenameOf(p: string): string {
  const lastSep = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
  return lastSep >= 0 ? p.slice(lastSep + 1) : p;
}

/**
 * Resolve which of ``paths`` no longer exist on disk via
 * ``POST /api/files/exists``. Returns a Set of the requested strings the
 * backend reported as missing (gone, or outside ``~/``). Optimistic
 * while in flight — nothing is marked missing until the check returns,
 * so a real file never flickers as "moved" on open.
 */
function useMissingPaths(paths: string[], revalidateTick: number): Set<string> {
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // Stable dependency: re-check when the set of paths changes. JSON (not a
  // join) so paths containing spaces round-trip intact. ``revalidateTick`` is
  // an extra trigger so we re-check the *same* path set on demand — a file can
  // be created or removed on disk without the reference list changing, which
  // would otherwise leave a stale "moved / removed" (or actionable) state until
  // reload.
  const key = useMemo(() => JSON.stringify([...paths].sort()), [paths]);

  useEffect(() => {
    const list = JSON.parse(key) as string[];
    if (list.length === 0) {
      setMissing(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/files/exists", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paths: list }),
        });
        const data = (await res.json()) as { statuses?: Record<string, boolean> };
        if (cancelled) return;
        const gone = new Set<string>();
        for (const [p, ok] of Object.entries(data.statuses ?? {})) {
          if (!ok) gone.add(p);
        }
        setMissing(gone);
      } catch {
        // Can't verify — leave everything actionable; the click handler
        // surfaces the error if the file really is gone.
        if (!cancelled) setMissing(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, revalidateTick]);

  return missing;
}

interface ArtifactsPanelProps {
  /** When true, renders only the section list without header/close (used inside RightPanel). */
  embedded?: boolean;
}

export function ArtifactsPanel({ embedded = false }: ArtifactsPanelProps) {
  const artifacts = useChatStore((s) => s.artifacts);
  const messages = useChatStore((s) => s.messages);
  const isAgentRunning = useChatStore((s) => s.isAgentRunning);
  const closePanel = useChatStore((s) => s.closeRightPanel);
  const saveWidgetAsArtifact = useChatStore((s) => s.saveWidgetAsArtifact);

  // The one Share dialog for whichever card requested it (null = closed).
  const [shareFor, setShareFor] = useState<{ filename: string; target: ShareTarget } | null>(null);
  const sharePath = (path: string, filename: string) => {
    const target = convRelOf(path);
    if (target) setShareFor({ filename, target });
  };

  // Auto-refresh the file list's on-disk validation. New artifacts + tool-
  // referenced paths already stream into the list live (``artifacts_update``
  // WS + streaming messages), but a file can be written or removed while the
  // reference set is unchanged — so poll existence gently during a turn and
  // re-check once when it settles, instead of going stale until reload.
  const [revalidateTick, setRevalidateTick] = useState(0);
  useEffect(() => {
    if (!isAgentRunning) {
      setRevalidateTick((t) => t + 1); // final settle after the turn ends
      return;
    }
    const id = setInterval(() => setRevalidateTick((t) => t + 1), 4000);
    return () => clearInterval(id);
  }, [isAgentRunning]);

  // Basenames of every file already shown under "Artifacts". Both the
  // Widgets and Other Files sections de-dup against this so a file that's
  // been saved as an artifact never appears twice. We key on the artifact's
  // *path basename* (e.g. ``shortlist.md``) — not its display ``filename``
  // (e.g. "Sritam — Germany Shortlist"), which never matches the basename a
  // Write/render_ui tool_use carried — falling back to ``filename`` for
  // artifacts streamed without a ``file_path``.
  const artifactBasenames = useMemo(
    () => new Set(artifacts.map((a) => (a.file_path ? basenameOf(a.file_path) : a.filename))),
    [artifacts],
  );

  // Interactive widgets get their own section so they're findable instead
  // of buried among Read/Write side-effect paths in "Other Files".
  const widgets = useMemo(
    () => extractWidgets(messages, artifactBasenames),
    [messages, artifactBasenames],
  );

  const otherFiles = useMemo(
    () => extractOtherFiles(messages, artifactBasenames),
    [messages, artifactBasenames],
  );

  // Verify every path-backed file still exists so we can render gone
  // files as "moved / removed" rather than dead, silently-failing cards.
  const checkPaths = useMemo(() => {
    const out: string[] = [];
    for (const a of artifacts) if (a.file_path) out.push(a.file_path);
    for (const w of widgets) out.push(w.path);
    for (const f of otherFiles) out.push(f.path);
    return out;
  }, [artifacts, widgets, otherFiles]);
  const missing = useMissingPaths(checkPaths, revalidateTick);

  const totalFiles = artifacts.length + widgets.length + otherFiles.length;

  const body = (
    <div className="p-3 space-y-4">
      {artifacts.length > 0 && (
        <section>
          <SectionHeader label="Artifacts" count={artifacts.length} />
          <div className="space-y-2">
            {artifacts.map((a) => (
              <FileCard
                key={a.id}
                name={a.filename}
                ext={a.file_type}
                subtitle={`${a.file_type.toUpperCase()} file · Generated`}
                missing={!!a.file_path && missing.has(a.file_path)}
                onOpen={() => artifactAction(a.id, "open", a.filename)}
                onReveal={() => artifactAction(a.id, "reveal")}
                onDownload={() => downloadArtifactSmart(a.id, a.filename)}
                onShare={() => setShareFor({ filename: a.filename, target: { artifactId: a.id } })}
              />
            ))}
          </div>
        </section>
      )}

      {widgets.length > 0 && (
        <section>
          <SectionHeader label="Widgets" count={widgets.length} />
          <div className="space-y-2">
            {widgets.map((w) => (
              <FileCard
                key={w.path}
                name={w.title}
                ext={w.ext}
                icon="🎨"
                subtitle="Interactive widget"
                missing={missing.has(w.path)}
                onOpen={() => pathAction(w.path, "open")}
                onReveal={() => pathAction(w.path, "reveal")}
                onDownload={() => downloadPathSmart(w.path)}
                onShare={() => sharePath(w.path, w.title)}
                onSave={async () => {
                  await saveWidgetAsArtifact(w.genuiId);
                }}
              />
            ))}
          </div>
        </section>
      )}

      {otherFiles.length > 0 && (
        <section>
          <SectionHeader label="Other Files" count={otherFiles.length} />
          <div className="space-y-2">
            {otherFiles.map((f) => (
              <FileCard
                key={f.path}
                name={f.name}
                ext={f.ext}
                subtitle={otherFileSubtitle(f)}
                missing={missing.has(f.path)}
                onOpen={() => pathAction(f.path, "open")}
                onReveal={() => pathAction(f.path, "reveal")}
                onDownload={() => downloadPathSmart(f.path)}
                onShare={() => sharePath(f.path, f.name)}
              />
            ))}
          </div>
        </section>
      )}

      {totalFiles === 0 && (
        <EmptyState />
      )}

      {shareFor && (
        <ShareDialog
          filename={shareFor.filename}
          target={shareFor.target}
          onClose={() => setShareFor(null)}
        />
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="h-full flex flex-col bg-surface">
      <div className="flex items-center justify-between px-4 py-3 border-b border-space-30 shrink-0">
        <div>
          <h3 className="text-sm font-bold text-space">Files</h3>
          <p className="text-xs text-space-40 mt-0.5">
            {totalFiles} file{totalFiles !== 1 ? "s" : ""} in this conversation
          </p>
        </div>
        <button
          onClick={closePanel}
          className="p-1.5 text-space-40 hover:text-space hover:bg-space-20 rounded-lg transition-all duration-150"
          title="Close panel"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto scrollbar-on-hover">
        {body}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-6">
      <div className="w-12 h-12 rounded-xl bg-space-20 flex items-center justify-center mb-3">
        <svg className="w-6 h-6 text-space-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
        </svg>
      </div>
      <p className="text-sm text-space-50">No files yet</p>
      <p className="text-xs text-space-40 mt-1">Files you upload and files the agent generates will appear here.</p>
    </div>
  );
}
